import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { useRoleState } from '../context/RoleContext';
import { useEmployeeId } from '../context/RoleContext';
import s from './Plus.module.css';
import { ReactComponent as HomeIcon } from '../icons/main_screen_icon.svg';
import { ReactComponent as NewsIcon } from '../icons/news_icon.svg';
import { ReactComponent as ChatIcon } from '../icons/chats_icon.svg';
import { ReactComponent as RatingsIcon } from '../icons/ratings_icon.svg';
import { ReactComponent as ProjectsIcon } from '../icons/projects_icon.svg';
import { ReactComponent as AppinfoBtnIcon } from '../icons/about_app.svg';
import { ReactComponent as AdminBtnIcon } from '../icons/admin_btn.svg';
import { ReactComponent as SettingsIcon } from '../icons/settings.svg';
import { ReactComponent as SidebarLogo } from '../icons/app_white_logo.svg';
import { ReactComponent as ClickboardCheckIcon } from '../icons/clickboard_check.svg';
import { ReactComponent as InfoSquareIcon } from '../icons/info_square.svg';
import { ReactComponent as FileShieldIcon } from '../icons/file_shield.svg';
import { ReactComponent as DexterIcon } from '../icons/dexter_icon.svg';
import { ReactComponent as TrophyIcon } from '../icons/trophy.svg';
import { ReactComponent as TasksIcon } from '../icons/tasks_icon.svg';
import { useMe } from './Plus';
import { useNavigate } from 'react-router-dom';
import { EmployeeTaskStatsAvatar } from './UI';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';
import { logout } from '../utils/auth';

function MiniSidebarLogo(props) {
  return (
    <svg
      width="35"
      height="52"
      viewBox="0 0 35 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M0 0H25C30.5228 0 35 4.47715 35 10V12H0V0Z"
        fill="white"
      />
      <rect
        y="14"
        width="35"
        height="11"
        fill="#BD23DB"
      />
      <path
        d="M0 27H35V28C35 33.5228 30.5228 38 25 38H0V27Z"
        fill="#BD23DB"
      />
      <path
        d="M0 40H17V42C17 47.5228 12.5228 52 7 52H0V40Z"
        fill="#BD23DB"
      />
    </svg>
  );
}

function CollapseIcon({ collapsed }) {
  if (collapsed) {
    return (
      <svg width="100%" height="100%" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M21 9.24995H12M21 3.99995L12 3.99995M21 14.75H3M21 20H3M4.28 2.95995L8.14667 5.85995C8.43616 6.07707 8.5809 6.18563 8.63266 6.31872C8.678 6.43529 8.678 6.56462 8.63266 6.68119C8.5809 6.81427 8.43616 6.92283 8.14667 7.13995L4.28 10.04C3.86802 10.3489 3.66203 10.5034 3.48961 10.4998C3.33956 10.4967 3.19885 10.4264 3.10632 10.3082C3 10.1724 3 9.91493 3 9.39995V3.59995C3 3.08498 3 2.82749 3.10632 2.6917C3.19885 2.57354 3.33956 2.50318 3.48961 2.50006C3.66203 2.49648 3.86802 2.65097 4.28 2.95995Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg width="100%" height="100%" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 9.24995H3M12 3.99995L3 3.99995M21 14.75H3M21 20H3M19.72 2.95995L15.8533 5.85995C15.5638 6.07707 15.4191 6.18563 15.3673 6.31872C15.322 6.43529 15.322 6.56462 15.3673 6.68119C15.4191 6.81427 15.5638 6.92283 15.8533 7.13995L19.72 10.04C20.132 10.3489 20.338 10.5034 20.5104 10.4998C20.6604 10.4967 20.8012 10.4264 20.8937 10.3082C21 10.1724 21 9.91493 21 9.39995V3.59995C21 3.08498 21 2.82749 20.8937 2.6917C20.8012 2.57354 20.6604 2.50318 20.5104 2.50006C20.338 2.49648 20.132 2.65097 19.72 2.95995Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M16 17L21 12M21 12L16 7M21 12H9M9 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const SIDEBAR_EXPANDED_WIDTH = '275px';
const SIDEBAR_COLLAPSED_WIDTH = '60px';
const SIDEBAR_BASE_PADDING = '10px';
const SIDEBAR_EXPANDED_PADDING = `calc(${SIDEBAR_BASE_PADDING} + 220px)`;
const DESKTOP_SIDEBAR_MQ = '(min-width: 768px)';

function applySidebarLayout(collapsed) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const isTabletOrDesktop = window.matchMedia(DESKTOP_SIDEBAR_MQ).matches;
  if (!isTabletOrDesktop) return;

  root.style.setProperty(
    '--sidebar-width',
    collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH
  );
  root.style.setProperty(
    '--sidebar-padding-left',
    collapsed ? SIDEBAR_BASE_PADDING : SIDEBAR_EXPANDED_PADDING
  );
}

function BottomNav() {
  const employeeId = useEmployeeId();
  const { role } = useRoleState();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });

  const me = useMe(employeeId);
  const navigate = useNavigate();
  const employeeTaskStatsIds = useMemo(
    () => (employeeId ? [employeeId] : []),
    [employeeId]
  );
  const employeeTaskStatsMap = useEmployeeTaskStats(employeeTaskStatsIds);

  useEffect(() => {
    applySidebarLayout(collapsed);

    const mq = window.matchMedia(DESKTOP_SIDEBAR_MQ);
    const syncLayout = () => applySidebarLayout(collapsed);

    mq.addEventListener('change', syncLayout);
    window.addEventListener('resize', syncLayout);

    return () => {
      mq.removeEventListener('change', syncLayout);
      window.removeEventListener('resize', syncLayout);
    };
  }, [collapsed]);

  useEffect(() => {
    const updateKeyboardState = () => {
      const vv = window.visualViewport;
      if (!vv) return;

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (!isMobile) {
        document.body.classList.remove('bottom-nav-keyboard-open');
        return;
      }

      const keyboardOpen = Math.abs(window.innerHeight - vv.height) > 150;
      document.body.classList.toggle('bottom-nav-keyboard-open', keyboardOpen);
    };

    updateKeyboardState();

    window.visualViewport?.addEventListener('resize', updateKeyboardState);
    window.addEventListener('resize', updateKeyboardState);
    window.addEventListener('orientationchange', updateKeyboardState);

    return () => {
      window.visualViewport?.removeEventListener('resize', updateKeyboardState);
      window.removeEventListener('resize', updateKeyboardState);
      window.removeEventListener('orientationchange', updateKeyboardState);
      document.body.classList.remove('bottom-nav-keyboard-open');
    };
  }, []);
  
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebar_collapsed', next ? '1' : '0');
      } catch { }
      return next;
    });
  };

  const handleLogout = () => {
    if (!window.confirm('Выйти из аккаунта?')) return;
    logout();
  };

  const mobileNavItems = useMemo(
    () => [
      {
        to: '/plus',
        label: 'Главная',
        ariaLabel: 'Главная',
        icon: <HomeIcon className={s.navIcon} />,
      },
      {
        to: '/news',
        label: 'Новости',
        ariaLabel: 'Новости',
        icon: <NewsIcon className={s.navIcon} />,
      },
      {
        to: '/chats',
        label: 'Чаты',
        ariaLabel: 'Чаты',
        icon: <ChatIcon className={s.navIcon} />,
      },
      {
        to: '/ratings',
        label: 'Рейтинги',
        ariaLabel: 'Рейтинги',
        icon: <RatingsIcon className={s.navIcon} />,
      },
      {
        to: '/battles',
        label: 'Сражения',
        ariaLabel: 'Сражения',
        icon: <TrophyIcon className={s.drawerIconSvg} />,
        sidebarOnly: true,
      },
      {
        to: `/profile/${employeeId || ''}`,
        label: 'Проекты',
        ariaLabel: 'Проекты',
        icon: <ProjectsIcon className={s.navIcon} />,
        sidebarOnly: true,
      },
      {
        to: '/tasks',
        label: 'Задачи',
        ariaLabel: 'Задачи',
        icon: <TasksIcon className={s.navIcon} />,
      },
    ],
    [employeeId]
  );

  const desktopExtraItems = useMemo(() => {
    const items = [
      {
        to: '/structure',
        label: 'Структура',
        ariaLabel: 'Структура',
        icon: <AppinfoBtnIcon className={s.drawerIconSvg} />,
      },
      {
        to: '/regulations',
        label: 'Регламенты',
        ariaLabel: 'Регламенты',
        icon: <ClickboardCheckIcon className={s.drawerIconSvg} />,
      },
      {
        to: '/dexter',
        label: 'Dexter',
        ariaLabel: 'Dexter',
        icon: <DexterIcon className={s.drawerIconSvg} />,
      },
      {
        to: '/settings',
        label: 'Настройки',
        ariaLabel: 'Настройки',
        icon: <SettingsIcon className={s.drawerIconSvg} />,
      },
      {
        to: '/license',
        label: 'Лицензионное соглашение',
        ariaLabel: 'Лицензионное соглашение',
        icon: <FileShieldIcon className={s.drawerIconSvg} />,
      },
      {
        to: '/about',
        label: 'Задачи 1C',
        ariaLabel: 'Задачи 1C',
        icon: <InfoSquareIcon className={s.drawerIconSvg} />,
      },
    ];

    if (role === 'admin') {
      items.push({
        to: '/admin',
        label: 'Админка',
        ariaLabel: 'Админка',
        icon: <AdminBtnIcon className={s.navIcon} />,
      });
    }

    return items;
  }, [role]);

  const nav = (
    <nav
      className={`${s.bottomNav} bottomNav ${collapsed ? s.bottomNavCollapsed : s.bottomNavExpanded}`}
      aria-label="Навигация"
    >
      <div className={s.sidebarBrand}>
        {collapsed ? (
          <button
            type="button"
            className={s.sidebarBrandBtn}
            onClick={toggleCollapsed}
            aria-label="Развернуть меню"
            title="Развернуть меню"
          >
            <MiniSidebarLogo className={s.drawerLogoMini} aria-hidden="true" />
          </button>
        ) : (
          <SidebarLogo className={s.drawerLogo} aria-label="РАНГИ" />
        )}
      </div>

      {!collapsed && <div className={`${s.navSectionDivider} ${s.navSectionDividerTop}`} />}

      {mobileNavItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `${s.navBtn} ${isActive ? s.navBtnActive : ''}${item.sidebarOnly ? ` ${s.sidebarOnlyNavItem}` : ''}`
          }
          aria-label={item.ariaLabel}
        >
          <span className={s.iconWrap}>{item.icon}</span>
          <span className={s.navLabel}>{item.label}</span>
        </NavLink>
      ))}

      {!collapsed &&
        <>
          <div className={s.navSectionDivider} />
          <div className={s.desktopOnlyBlock}>
            {desktopExtraItems.map((item) => (
              <React.Fragment key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) => `${s.navBtn} ${isActive ? s.navBtnActive : ''}`}
                  aria-label={item.ariaLabel}
                >
                  <span className={s.iconWrap}>{item.icon}</span>
                  <span className={s.navLabel}>{item.label}</span>
                </NavLink>

                {item.label === 'Dexter' && <div className={s.navSectionDivider} />}
              </React.Fragment>
            ))}
          </div>
        </>
      }

      {me && !collapsed && (
        <div
          className={s.drawerUserDesktop}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/account/me')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') navigate('/account/me');
          }}
          title="Открыть мой аккаунт"
        >
          <EmployeeTaskStatsAvatar
            className={s.drawerUserAvatar}
            employeeId={employeeId}
            stats={employeeTaskStatsMap.get(Number(employeeId))}
            fetchStats={false}
            avatar={me.avatar}
            size={90}
            alt={me.name || 'avatar'}
          />

          <div className={s.drawerUserInfo}>
            <div className={s.drawerUserName}>
              <span className={s.drawerUserFirst}>{(me.name || '—').split(' ')[0] || '—'}</span>
              <span className={s.drawerUserLast}>
                {(me.name || '').split(' ').slice(1).join(' ')}
              </span>
            </div>
          </div>

          {me.email && <div className={s.drawerUserEmail}>{me.email}</div>}
        </div>
      )}

      <div className={s.sidebarFooter}>
        <button
          type="button"
          className={s.sidebarToggle}
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
        >
          <span className={s.sidebarToggleIcon}>
            <CollapseIcon collapsed={collapsed} />
          </span>
          <span className={s.navLabel}>
            {collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          </span>
        </button>

        {me && (
          <button
            type="button"
            className={s.sidebarToggle}
            onClick={handleLogout}
            aria-label="Выйти из аккаунта"
            title="Выйти из аккаунта"
          >
            <span className={s.sidebarToggleIcon}>
              <LogoutIcon />
            </span>
            <span className={s.navLabel}>Выйти</span>
          </button>
        )}
      </div>
    </nav>
  );

  if (typeof document === 'undefined') return nav;
  return createPortal(nav, document.body);
}

export default BottomNav;

